import { useEffect, useRef, useState } from 'react'

import { renewSession } from '../api/client.ts'
import type { ConnectionState } from './types.ts'

/**
 * O núcleo de conexão SSE, compartilhado por `useMetricsStream` e `useLogStream`.
 *
 * É a máquina de recuperação que levou trabalho para ficar certa, extraída para
 * um lugar só (§0.5): sonda o status com um `fetch` abortado (porque
 * `EventSource` fecha em qualquer não-2xx e NÃO dá acesso ao status), distingue
 * `401` de `403` de rede fora, renova o token UMA vez por abertura para não virar
 * laço, reabre SEM backoff depois de renovar, e aplica backoff exponencial com
 * jitter no resto.
 *
 * O que difere entre os dois streams entra por configuração, não por cópia:
 *  - `staleAfterMs`, porque o de métricas tica a cada 2 s (10 s de silêncio é
 *    falha) e o de logs pode calar por minutos num agente quieto (§4.4);
 *  - `events`, que decodificam o payload e devolvem se aquele evento conta como
 *    sinal de vida — o de métricas devolve `false` num payload malformado, para
 *    não pintar "ao vivo" sobre número congelado; o de logs conta qualquer
 *    tráfego;
 *  - `touchOnAnyMessage`, que alimenta o relógio de frescor com o handler
 *    genérico de `message` além dos nomeados (§4.4).
 *
 * Ele também carimba `data-connection` em <html>, que é o que congela cada número
 * na tela (ver `.live-value` no index.css) — fazer isso aqui mantém o atributo do
 * DOM e a máquina de estado impossíveis de dessincronizar.
 */

const RECONNECT_BASE_MS = 800
const RECONNECT_MAX_MS = 15_000

export interface SseConnectionConfig {
  url: string
  /** Silêncio tolerado antes de 'stale'. Difere por stream de propósito. */
  staleAfterMs: number
  /**
   * Handlers por nome de evento. Cada um consome o payload e devolve se aquele
   * evento conta como SINAL DE VIDA. Ver a descrição do hook.
   */
  events: Record<string, (raw: string, at: number) => boolean>
  /**
   * Há dado na tela? Decide, na abertura, entre 'connecting' (nada ainda; dizer
   * "ao vivo" seria mentira) e 'stale' (transporte de pé, à espera do 1º payload).
   */
  hasData?: () => boolean
  /**
   * Qualquer tráfego conta como vida — inclusive o handler genérico de `message`,
   * que apanha um evento cujo nome mudou no servidor e este cliente ainda não
   * conhece. Ligado no stream de logs (§4.4), desligado no de métricas.
   */
  touchOnAnyMessage?: boolean
}

export interface SseConnection {
  connection: ConnectionState
  /** Relógio do navegador (epoch ms) da última vez que algo contou como vida. */
  lastEventAt: number | null
  /** Reconexões desde a montagem — diagnóstico do rodapé. */
  reconnects: number
  /** Relógio do navegador da última abertura de transporte. */
  openedAt: number | null
}

interface CoreState {
  connection: ConnectionState
  lastEventAt: number | null
  reconnects: number
  openedAt: number | null
}

const INITIAL: CoreState = {
  connection: 'connecting',
  lastEventAt: null,
  reconnects: 0,
  openedAt: null,
}

export function useSseConnection(config: SseConnectionConfig): SseConnection {
  const [state, setState] = useState<CoreState>(INITIAL)

  // A configuração é lida por referência dentro do efeito. Depender dela no
  // array do efeito reabriria a conexão a cada render — e cada reabertura é uma
  // linha no log de auditoria do servidor (§0.4). O efeito só reage a mudanças
  // reais de identidade do stream: url, janela de frescor e política de frescor.
  const cfgRef = useRef(config)
  cfgRef.current = config

  // O watchdog de frescor lê isto sem reassinar a cada tique.
  const lastEventAtRef = useRef<number | null>(null)

  const { url, staleAfterMs, touchOnAnyMessage = false } = config

  useEffect(() => {
    let disposed = false
    let source: EventSource | null = null
    let reopenTimer: ReturnType<typeof setTimeout> | undefined
    let attempt = 0
    let listeners: Array<{ name: string; listener: (event: Event) => void }> = []
    /**
     * Já renovamos a credencial desde a última abertura bem-sucedida?
     *
     * Sem esta trava, um 401 que a renovação não resolve viraria um laço apertado
     * de refresh e reconexão. Uma renovação por queda; a segunda recusa seguida
     * cai no backoff normal.
     */
    let renewedSinceOpen = false

    const touch = (at: number): void => {
      lastEventAtRef.current = at
      setState((prev) =>
        prev.connection === 'live' && prev.lastEventAt === at
          ? prev
          : { ...prev, connection: 'live', lastEventAt: at },
      )
    }

    const subscribe = (es: EventSource, name: string): void => {
      const listener = (event: Event): void => {
        const message = event as MessageEvent<unknown>
        if (typeof message.data !== 'string') return
        const at = Date.now()
        // Decodifica primeiro, conta depois: um payload que o decodificador
        // recusa não pode marcar o stream como vivo — a não ser que a política do
        // stream (logs) trate qualquer tráfego como vida.
        const heartbeat = cfgRef.current.events[name]?.(message.data, at) ?? false
        if (heartbeat || touchOnAnyMessage) touch(at)
      }
      es.addEventListener(name, listener)
      listeners.push({ name, listener })
    }

    const teardown = (): void => {
      if (source !== null) {
        for (const { name, listener } of listeners) source.removeEventListener(name, listener)
        source.onopen = null
        source.onerror = null
        source.onmessage = null
        source.close()
      }
      listeners = []
      source = null
    }

    const scheduleReopen = (): void => {
      if (disposed) return
      attempt += 1
      const backoff = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** (attempt - 1))
      // Jitter mantém uma frota de painéis fora de lockstep na reconexão.
      const delay = backoff * (0.75 + Math.random() * 0.5)
      reopenTimer = setTimeout(open, delay)
    }

    /**
     * Por que o stream foi recusado.
     *
     * `EventSource` fecha em qualquer não-2xx e nunca diz o status. Sem isto o
     * console não distingue rede fora (esperar), token vencido (renovar e repetir)
     * e falta de papel (insistir é inútil, e a tela tem que dizer isso). A sonda é
     * uma requisição à mesma URL, abortada assim que a linha de status chega — o
     * corpo é um stream infinito e nunca é lido.
     */
    const probe = async (): Promise<'ok' | 'unauthorized' | 'forbidden' | 'unknown'> => {
      const controller = new AbortController()
      try {
        const response = await fetch(url, {
          credentials: 'include',
          headers: { Accept: 'text/event-stream' },
          cache: 'no-store',
          signal: controller.signal,
        })
        controller.abort()

        if (response.status === 401) return 'unauthorized'
        if (response.status === 403) return 'forbidden'
        return response.ok ? 'ok' : 'unknown'
      } catch {
        return 'unknown'
      }
    }

    const recover = async (): Promise<void> => {
      const verdict = await probe()
      if (disposed) return

      if (verdict === 'forbidden') {
        // Terminal: nenhuma reconexão concede um papel. O laço de backoff para.
        setState((prev) => ({ ...prev, connection: 'forbidden' }))
        return
      }

      if (verdict === 'unauthorized') {
        if (renewedSinceOpen) {
          scheduleReopen()
          return
        }

        try {
          // Regrava o cookie athena.jwt, que é como o EventSource autentica.
          renewedSinceOpen = true
          await renewSession()
        } catch {
          // A sessão acabou; o provider de autenticação já foi avisado e leva o
          // operador ao login. Insistir aqui só produziria mais 401.
          return
        }
        if (disposed) return
        // Sem backoff: a credencial acabou de ser renovada.
        open()
        return
      }

      scheduleReopen()
    }

    function open(): void {
      if (disposed) return
      teardown()

      const es = new EventSource(url, { withCredentials: true })
      source = es

      es.onopen = (): void => {
        attempt = 0
        renewedSinceOpen = false
        setState((prev) => ({
          ...prev,
          // Transporte de pé, mas nenhum payload ainda. Dizer 'live' aqui seria
          // mentira; e se já há dado na tela, 'stale' — números velhos à espera.
          connection: cfgRef.current.hasData?.() ? 'stale' : 'connecting',
          openedAt: Date.now(),
        }))
      }

      es.onerror = (): void => {
        if (disposed) return
        setState((prev) => ({
          ...prev,
          connection: 'disconnected',
          reconnects: prev.reconnects + 1,
        }))
        // CLOSED significa que o navegador desistiu (erro HTTP, content-type
        // errado, 401/403). Ele não vai tentar de novo, então o próximo passo é
        // nosso — e depende do porquê, então vamos descobrir.
        if (es.readyState === EventSource.CLOSED) void recover()
      }

      // Qualquer tráfego genérico conta como vida quando a política pede (§4.4).
      if (touchOnAnyMessage) es.onmessage = (): void => touch(Date.now())

      for (const name of Object.keys(cfgRef.current.events)) subscribe(es, name)
    }

    open()

    return () => {
      disposed = true
      if (reopenTimer !== undefined) clearTimeout(reopenTimer)
      teardown()
    }
  }, [url, staleAfterMs, touchOnAnyMessage])

  // Watchdog de frescor. Um timer, e só escreve estado numa transição real, então
  // um stream quieto custa zero renders.
  useEffect(() => {
    const id = setInterval(() => {
      setState((prev) => {
        if (
          prev.connection === 'disconnected' ||
          prev.connection === 'connecting' ||
          // Terminal: um watchdog de frescor não tem nada a dizer sobre um papel
          // que falta.
          prev.connection === 'forbidden'
        ) {
          return prev
        }
        const last = lastEventAtRef.current
        if (last === null) return prev
        const stale = Date.now() - last > staleAfterMs
        const next: ConnectionState = stale ? 'stale' : 'live'
        return next === prev.connection ? prev : { ...prev, connection: next }
      })
    }, 1_000)
    return () => clearInterval(id)
  }, [staleAfterMs])

  // Override só de desenvolvimento, para inspecionar os estados sem arranjar uma
  // queda real: ?connection=stale|disconnected|connecting|forbidden. Em build de
  // produção `import.meta.env.DEV` é estaticamente false e o ramo some.
  const forced = import.meta.env.DEV ? forcedConnection() : null
  const connection = forced ?? state.connection

  // Fonte única de verdade do tratamento de congelamento (ver index.css).
  useEffect(() => {
    document.documentElement.dataset['connection'] = connection
  }, [connection])

  // Sem stream montado não há o que congelar: telas que não dependem de SSE (a
  // visão geral do cliente, a gestão) não podem herdar o "parado" de um stream
  // que já foi desmontado.
  useEffect(
    () => () => {
      delete document.documentElement.dataset['connection']
    },
    [],
  )

  return {
    connection,
    lastEventAt: state.lastEventAt,
    reconnects: state.reconnects,
    openedAt: state.openedAt,
  }
}

function forcedConnection(): ConnectionState | null {
  if (typeof window === 'undefined') return null
  const requested = new URLSearchParams(window.location.search).get('connection')
  const states: readonly ConnectionState[] = [
    'connecting',
    'live',
    'stale',
    'disconnected',
    'forbidden',
  ]
  return states.find((candidate) => candidate === requested) ?? null
}
